import { useLogin, usePrivy } from '@privy-io/react-auth';
import { SettingsIcon } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useModalStore } from '@/store/use-modal-store';
import { SettingsTab } from '@/types';
import { handleUserForStore } from '@/utils/handle-store-data';

import ChartSettings from './settings/chart';
import Notifications from './settings/notifications';
import TradingSettings from './settings/trading';

export default function Settings() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('settings');

  const activeTab = useModalStore((state) => state.activeSettingsTab);

  const navigate = useNavigate();

  const { ready, authenticated } = usePrivy();
  const { login } = useLogin({
    onComplete: ({ user }) => handleUserForStore(user),
    onError: (_) => localStorage.removeItem('settings'),
  });

  useEffect(() => {
    if (!ready || !authenticated) return;
    const initialTab = localStorage.getItem('settings') as SettingsTab;
    localStorage.removeItem('settings');
    const values = Object.values(SettingsTab);
    if (!values.includes(initialTab)) return;
    useModalStore.setState({ activeSettingsTab: initialTab });
  }, [ready, authenticated]);

  useEffect(() => {
    if (!initialTab || !ready) return;
    const newTab = initialTab as SettingsTab;
    const values = Object.values(SettingsTab);
    if (!values.includes(newTab)) return;
    const params = new URLSearchParams(searchParams);
    params.delete('settings');
    const search = params.toString();
    navigate({ search: search.length > 0 ? '?' + search : '' });
    if (authenticated) {
      useModalStore.setState({ activeSettingsTab: newTab });
    } else {
      localStorage.setItem('settings', newTab);
      login();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, authenticated, initialTab]);

  return (
    <Dialog
      open={!!activeTab}
      onOpenChange={(open) =>
        useModalStore.setState({
          activeSettingsTab: open ? activeTab ?? SettingsTab.TRADING : null,
        })
      }
    >
      <DialogTrigger className="mx-2 flex items-center gap-2 p-4 text-sm sm:mx-4">
        <SettingsIcon className="size-5" />
      </DialogTrigger>
      <DialogContent
        className="overflow-hidden sm:max-w-6xl sm:rounded-3xl"
        closeClassName="top-10 right-10"
      >
        <DialogHeader className="px-6 pb-5 pt-10 sm:px-10">
          <DialogTitle className="text-3xl">
            {t('settingsDropdown.settings')}
          </DialogTitle>
          <DialogDescription>
            {t('settingsDropdown.description')}
          </DialogDescription>
        </DialogHeader>
        <Tabs
          value={activeTab ?? SettingsTab.TRADING}
          onValueChange={(value) =>
            useModalStore.setState({ activeSettingsTab: value as SettingsTab })
          }
          className="flex flex-col items-start gap-10 sm:gap-0 sm:pl-10 md:grid"
          style={{ gridTemplateColumns: '1fr 4fr' }}
        >
          <TabsList className="max-md:w-full max-md:justify-start max-md:px-6 md:flex-col">
            <TabsTrigger
              className="w-full justify-start rounded-md border-y-0 px-6 py-3 text-left text-base normal-case data-[state=active]:border-b-0 data-[state=active]:bg-muted data-[state=active]:text-foreground max-md:max-w-max"
              value={SettingsTab.TRADING}
            >
              {t('settingsDropdown.trading')}
            </TabsTrigger>
            <TabsTrigger
              className="w-full justify-start rounded-md border-y-0 px-6 py-3 text-left text-base normal-case data-[state=active]:border-b-0 data-[state=active]:bg-muted data-[state=active]:text-foreground max-md:max-w-max"
              value={SettingsTab.CHART}
            >
              {t('settingsDropdown.chart')}
            </TabsTrigger>
            <TabsTrigger
              className="w-full justify-start rounded-md border-y-0 px-6 py-3 text-left text-base normal-case data-[state=active]:border-b-0 data-[state=active]:bg-muted data-[state=active]:text-foreground max-md:max-w-max"
              value={SettingsTab.NOTIFICATIONS}
            >
              {t('settingsDropdown.notifications')}
            </TabsTrigger>
          </TabsList>
          <TabsContent value={SettingsTab.TRADING} style={{ height: 336 }}>
            <ScrollArea className="h-full px-6 sm:px-10">
              <TradingSettings />
            </ScrollArea>
          </TabsContent>
          <TabsContent value={SettingsTab.CHART} style={{ height: 336 }}>
            <ScrollArea className="h-full px-6 sm:px-10">
              <ChartSettings />
            </ScrollArea>
          </TabsContent>
          <TabsContent
            value={SettingsTab.NOTIFICATIONS}
            style={{ height: 336 }}
            className="w-full"
          >
            <Notifications />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
